import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo, Icon, Eyebrow } from '../features/brand/Primitives';
import DemoRestaurantCard from '../features/demo/DemoRestaurantCard';
import { FAKE_RESTAURANTS } from '../features/demo/fakeData';
import { TAG_GROUPS, GROUP_ORDER, GROUP_PALETTE } from '../features/discovery/taxonomy';
import DemoModal from '../features/demo/DemoModal';
import { useAuthModal } from '../features/auth/AuthModalContext';
import { useI18n } from '../features/i18n';
import '../features/brand/cwm-tokens.css';

// Translation-key suffix for each taxonomy group. The TAG_GROUPS keys are
// display strings in English, but we can map them to stable i18n slugs so
// the picker renders the user's language while keeping the internal
// shape (which other consumers like filters key off) unchanged.
const GROUP_LABEL_KEY = {
  'Religious':                  'landing.group_religious',
  'Diet':                       'landing.group_diet',
  'Allergies':                  'landing.group_allergies',
  'Therapeutic':                'landing.group_therapeutic',
  'Lifestyle':                  'landing.group_lifestyle',
  'Ethical':                    'landing.group_ethical',
  'Life Stage':                 'landing.group_life_stage',
  'GLP-1 / Portions':           'landing.group_glp1_portions',
  'Sensory / Neurodivergent':   'landing.group_sensory_neurodivergent',
};

function LandingScreen() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const { openAuth } = useAuthModal();

  const onGetStarted = () => navigate('/discover');
  const onForRestaurants = () => navigate('/for-restaurants');
  const onWatchDemo = () => setDemoOpen(true);
  const onSignIn = () => openAuth('signin');
  const onPricing = () => navigate('/pricing');
  const onLoyalty = () => navigate('/loyalty');

  return (
    <div className="cwm-app">
      <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        <FloatingTags />

        <nav className="cwm-nav" style={{ position: 'relative', zIndex: 2 }}>
          <Logo variant="stacked-white" height={140} />
          {/* These used to be `<a href="#">` tags. href="#" jumps to the
              page top when JS fails and also trips the jsx-a11y
              anchor-is-valid rule. They're all JS-driven route changes,
              so <button> with link styling is the semantically-correct
              element. `cwm-nav-link` is a display-class on the button. */}
          <div className="cwm-nav-links">
            <button type="button" className="cwm-nav-link active" onClick={(e) => e.preventDefault()}>Discover</button>
            <button type="button" className="cwm-nav-link" onClick={onForRestaurants}>For restaurants</button>
            <button type="button" className="cwm-nav-link" onClick={onPricing}>Pricing</button>
            <button type="button" className="cwm-nav-link" onClick={onLoyalty}>Rewards</button>
            <button type="button" className="cwm-nav-link" onClick={onSignIn} style={{ fontWeight: 700 }}>Sign in</button>
            <button className="cwm-btn" onClick={onGetStarted}>Get Started</button>
          </div>
        </nav>

        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1.05fr 1fr',
            gap: 48,
            alignItems: 'center',
            padding: '32px 72px 64px',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div>
            <Eyebrow>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--cwm-blue-deep)', animation: 'cwmLandDot 1.4s infinite' }} />
              AI-powered discovery
            </Eyebrow>
            <h1
              style={{
                fontSize: 62,
                fontWeight: 900,
                letterSpacing: '-0.025em',
                lineHeight: 1.02,
                color: '#fff',
                margin: '16px 0 20px',
                textShadow: '0 4px 40px rgba(13,71,161,0.35)',
              }}
            >
              Never stress<br />
              about <span style={{ color: '#CFE8FF', fontStyle: 'italic', display: 'inline-block' }}>what to eat</span>.
            </h1>
            <p style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.5, color: 'rgba(255,255,255,0.94)', maxWidth: 520, margin: 0 }}>
              Chat with any restaurant's menu. Get recommendations tailored to your allergies, diet, and the mood you're in. We'll learn what works for you.
            </p>

            <CategoryPicker onGetStarted={onGetStarted} />

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button className="cwm-btn" onClick={onGetStarted} style={{ fontSize: 15 }}>
                <Icon name="explore" size={18} color="#fff" />
                Start discovering
              </button>
              <button className="cwm-btn-secondary" onClick={onWatchDemo} style={{ fontSize: 15 }}>
                <Icon name="play_circle" size={18} color="var(--cwm-blue-deep)" />
                Watch demo
              </button>
            </div>
            <div style={{ display: 'flex', gap: 22, marginTop: 24, color: '#fff', opacity: 0.95, position: 'relative', zIndex: 3 }}>
              <Stat n="2,400+" l="Restaurants" />
              <Divider />
              <Stat n="97" l="Dietary filters" />
              <Divider />
              <Stat n="4.9" l="App Store rating" />
            </div>
          </div>

          <LivePreview />
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} onGetStarted={onGetStarted} />
    </div>
  );
}

export function Divider() {
  return <div style={{ width: 1, background: 'rgba(255,255,255,0.3)' }} />;
}

export function FloatingTags() {
  const tags = [
    { l: 'Gluten-Free', t: 2, top: '12%', left: '52%', size: 13, rot: 6, delay: 0.6 },
    { l: 'Dairy-Free', t: 2, top: '46%', left: '92%', size: 13, rot: 10, delay: 2.1 },
    { l: 'Kosher', t: 0, top: '10%', left: '80%', size: 12, rot: -4, delay: 2.6 },
    { l: 'Pescatarian', t: 1, top: '72%', left: '50%', size: 13, rot: 8, delay: 3.1 },
    { l: 'Low-Sodium', t: 3, top: '88%', left: '82%', size: 12, rot: -9, delay: 3.6 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, overflow: 'hidden' }}>
      {tags.map((t, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: t.top,
            left: t.left,
            padding: '5px 11px',
            borderRadius: 999,
            background: `var(--cwm-grp-${t.t}-bg)`,
            color: `var(--cwm-grp-${t.t}-fg)`,
            border: `1px solid var(--cwm-grp-${t.t}-br)`,
            fontWeight: 800,
            fontSize: t.size,
            transform: `rotate(${t.rot}deg)`,
            opacity: 0.55,
            animation: `cwmLandFloat 6s ${t.delay}s ease-in-out infinite`,
            boxShadow: '0 4px 14px rgba(13,71,161,0.1)',
          }}
        >
          {t.l}
        </span>
      ))}
    </div>
  );
}

export function CategoryPicker({ onGetStarted }) {
  const [hovered, setHovered] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const { t } = useI18n();
  // Each group gets its English `name` (internal id, used by the rest of
  // the app for filter lookups) plus a translated `label` (display only).
  const labelFor = (name) => {
    const key = GROUP_LABEL_KEY[name];
    return key ? t(key) : name;
  };
  const groups = GROUP_ORDER.map((name) => ({
    name,
    label: labelFor(name),
    tags: TAG_GROUPS[name] || [],
    palette: GROUP_PALETTE[name] || { bg: '#fff', fg: '#1E9BFF', border: '#BCE7FF', active: '#1E9BFF' },
  }));
  return (
    <div style={{ marginTop: 24, maxWidth: 580 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 900,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.85)',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ width: 18, height: 1.5, background: 'rgba(255,255,255,0.4)' }} />
        {expanded ? t('landing.pick_tag_in', { category: labelFor(expanded).toLowerCase() }) : t('landing.pick_eyebrow')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'flex-start' }}>
        {groups.map((g, i) => {
          const isH = hovered === g.name && !expanded;
          const isExpanded = expanded === g.name;
          if (expanded && !isExpanded) return null;

          if (isExpanded) {
            return (
              <React.Fragment key={g.name}>
                <button
                  onClick={() => setExpanded(null)}
                  aria-label="Back"
                  title="Back to categories"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '7px 14px 7px 10px',
                    borderRadius: 999,
                    background: g.palette.active,
                    color: '#fff',
                    border: `1.5px solid ${g.palette.active}`,
                    fontWeight: 800,
                    fontSize: 12.5,
                    lineHeight: 1,
                    cursor: 'pointer',
                    boxShadow: `0 6px 18px ${g.palette.active}55, 0 2px 4px rgba(13,71,161,0.2)`,
                    animation: 'cwmPillExpand 220ms ease-out',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.28)',
                      fontSize: 13,
                      lineHeight: 1,
                      fontWeight: 900,
                    }}
                  >
                    ‹
                  </span>
                  {g.label}
                </button>
                {g.tags.map((tag, j) => (
                  <button
                    key={tag}
                    onClick={onGetStarted}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '6px 11px',
                      borderRadius: 999,
                      background: '#fff',
                      color: g.palette.fg,
                      border: `1.5px solid ${g.palette.border}`,
                      fontWeight: 800,
                      fontSize: 12,
                      lineHeight: 1,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(13,71,161,0.15)',
                      transition: 'transform 160ms ease, box-shadow 160ms ease',
                      animation: `cwmLandTagIn 360ms ${j * 22}ms 1 forwards`,
                      opacity: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 6px 14px ${g.palette.active}33`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,71,161,0.15)';
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: g.palette.active }} />
                    {tag}
                  </button>
                ))}
              </React.Fragment>
            );
          }

          return (
            <button
              key={g.name}
              onClick={() => setExpanded(g.name)}
              onMouseEnter={() => setHovered(g.name)}
              onMouseLeave={() => setHovered(null)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '7px 12px 7px 10px',
                borderRadius: 999,
                background: isH ? g.palette.active : g.palette.bg,
                color: isH ? '#fff' : g.palette.fg,
                border: `1.5px solid ${isH ? g.palette.active : g.palette.border}`,
                fontWeight: 800,
                fontSize: 12.5,
                lineHeight: 1,
                cursor: 'pointer',
                boxShadow: isH
                  ? `0 6px 18px ${g.palette.active}55, 0 2px 4px rgba(13,71,161,0.2)`
                  : '0 2px 8px rgba(13,71,161,0.15)',
                transform: isH ? 'translateY(-2px)' : 'translateY(0)',
                transition: 'all 180ms cubic-bezier(.2,.9,.3,1.1)',
                animation: `cwmLandTagIn 500ms ${i * 55}ms 1 forwards`,
                opacity: 0,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: isH ? '#fff' : g.palette.active,
                  boxShadow: isH ? '0 0 0 2px rgba(255,255,255,0.3)' : 'none',
                }}
              />
              {g.label}
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: isH ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.06)',
                  color: isH ? '#fff' : g.palette.fg,
                  opacity: isH ? 1 : 0.75,
                }}
              >
                {g.tags.length}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function LivePreview() {
  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 520 }}>
      <SwipeDeck />
      <ChatPeek />
    </div>
  );
}

function SwipeDeck() {
  const deck = FAKE_RESTAURANTS;
  const [top, setTop] = useState(0);
  const [phase, setPhase] = useState('idle');
  const [dir, setDir] = useState(1);

  useEffect(() => {
    let alive = true;
    let t1, t2, t3;
    const loop = () => {
      if (!alive) return;
      const nextDir = Math.random() > 0.35 ? 1 : -1;
      setDir(nextDir);
      t1 = setTimeout(() => { if (alive) setPhase('tilt'); }, 100);
      t2 = setTimeout(() => { if (alive) setPhase('fly'); }, 1400);
      t3 = setTimeout(() => {
        if (!alive) return;
        setTop((prev) => (prev + 1) % deck.length);
        setPhase('idle');
        loop();
      }, 2100);
    };
    loop();
    return () => {
      alive = false;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const order = [0, 1, 2].map((i) => deck[(top + i) % deck.length]);

  return (
    <div style={{ position: 'relative', width: 320, height: 440 }}>
      {order.slice().reverse().map((r, rev) => {
        const i = order.length - 1 - rev;
        const isTop = i === 0;
        const baseLift = i * 8;
        const baseScale = 1 - i * 0.04;

        let transform = `translateY(${baseLift}px) scale(${baseScale})`;
        let opacity = 1;
        if (isTop) {
          if (phase === 'tilt') transform = `translateX(${dir * 60}px) rotate(${dir * 10}deg)`;
          if (phase === 'fly') {
            transform = `translateX(${dir * 520}px) rotate(${dir * 22}deg)`;
            opacity = 0;
          }
        }

        return (
          <div
            key={`${r.id}-${top}-${i}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform,
              opacity,
              transition: isTop
                ? phase === 'fly'
                  ? 'transform 700ms cubic-bezier(.5,.1,.8,.2), opacity 600ms ease-out'
                  : 'transform 600ms cubic-bezier(.2,.9,.3,1.1)'
                : 'transform 400ms ease-out',
              zIndex: 10 - i,
              pointerEvents: 'none',
            }}
          >
            <div style={{ position: 'relative' }}>
              {isTop && phase !== 'idle' && (
                <div
                  style={{
                    position: 'absolute',
                    inset: -10,
                    borderRadius: 32,
                    background:
                      dir === 1
                        ? 'radial-gradient(closest-side, rgba(22,128,60,0.85), rgba(22,128,60,0) 75%)'
                        : 'radial-gradient(closest-side, rgba(220,38,38,0.85), rgba(220,38,38,0) 75%)',
                    filter: 'blur(18px)',
                    opacity: phase === 'fly' ? 0 : 1,
                    transition: 'opacity 350ms ease-out',
                    zIndex: -1,
                    pointerEvents: 'none',
                  }}
                />
              )}
              <DemoRestaurantCard r={r} featured />
              {isTop && phase !== 'idle' && (
                <div
                  style={{
                    position: 'absolute',
                    top: 28,
                    [dir === 1 ? 'right' : 'left']: 18,
                    padding: '6px 14px',
                    border: `3px solid ${dir === 1 ? 'var(--cwm-green)' : 'var(--cwm-skip)'}`,
                    borderRadius: 10,
                    color: dir === 1 ? 'var(--cwm-green)' : 'var(--cwm-skip)',
                    fontWeight: 900,
                    fontSize: 22,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    transform: `rotate(${dir === 1 ? 14 : -14}deg)`,
                    background: 'rgba(255,255,255,0.94)',
                    opacity: phase === 'tilt' ? 0.96 : 0.8,
                    transition: 'opacity 200ms',
                  }}
                >
                  {dir === 1 ? 'Save' : 'Skip'}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CHAT_SCRIPT = [
  { from: 'user', text: "I have a nut allergy and I'm GF. Anything safe?" },
  { from: 'bot', text: 'Yes! The cacio e pepe is fully GF and made in a separate pan. No nuts anywhere in that dish.', chips: ['Save this', 'Show allergens'] },
  { from: 'user', text: 'What about dessert?' },
  { from: 'bot', text: "The torta alla nocciola has hazelnuts, so I'll hide anything with tree nuts from your list.", chips: ['Thanks!', 'Update prefs'] },
];

function ChatPeek() {
  const [step, setStep] = useState(1);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      while (alive) {
        await wait(1800);
        if (!alive) return;
        setTyping(true);
        await wait(900);
        if (!alive) return;
        setTyping(false);
        setStep((s) => (s >= CHAT_SCRIPT.length ? 1 : s + 1));
      }
    };
    run();
    return () => { alive = false; };
  }, []);

  const visible = CHAT_SCRIPT.slice(0, step);

  return (
    <div
      style={{
        position: 'absolute',
        right: -28,
        bottom: -8,
        width: 298,
        transform: 'rotate(4deg)',
        filter: 'drop-shadow(0 24px 40px rgba(13,71,161,0.28))',
        zIndex: 20,
      }}
    >
      <div className="cwm-sheet" style={{ padding: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingBottom: 10, borderBottom: '1px solid var(--cwm-gray-100)' }}>
          <Logo variant="mascot" height={28} />
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)' }}>Giulia's menu</div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--cwm-green-text)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--cwm-green-text)' }} /> Online
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10, maxHeight: 240, overflow: 'hidden' }}>
          {visible.map((m, i) => (
            <ChatBubble key={i} {...m} fresh={i === visible.length - 1} />
          ))}
          {typing && <TypingBubble />}
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ from, text, chips, fresh }) {
  const isUser = from === 'user';
  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '86%',
        animation: fresh ? 'cwmPop 280ms ease-out both' : 'none',
      }}
    >
      <div
        style={{
          background: isUser ? 'linear-gradient(180deg,#1E9BFF,#0F7FE8)' : 'var(--cwm-blue-pale)',
          color: isUser ? '#fff' : 'var(--cwm-ink)',
          padding: '8px 12px',
          borderRadius: 14,
          borderBottomRightRadius: isUser ? 4 : 14,
          borderBottomLeftRadius: isUser ? 14 : 4,
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.4,
          boxShadow: isUser ? '0 4px 12px rgba(30,155,255,0.25)' : 'none',
        }}
      >
        {text}
      </div>
      {chips && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {chips.map((c) => (
            <span
              key={c}
              style={{
                background: '#fff',
                border: '1px solid var(--cwm-blue-light)',
                color: 'var(--cwm-blue-deep)',
                fontSize: 10.5,
                fontWeight: 800,
                padding: '4px 9px',
                borderRadius: 999,
              }}
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TypingBubble() {
  return (
    <div
      style={{
        alignSelf: 'flex-start',
        background: 'var(--cwm-blue-pale)',
        padding: '10px 14px',
        borderRadius: 14,
        borderBottomLeftRadius: 4,
        display: 'flex',
        gap: 4,
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: 'var(--cwm-blue-deep)',
            animation: `cwmDot 1.1s ${i * 0.15}s infinite ease-in-out`,
          }}
        />
      ))}
    </div>
  );
}

export function Stat({ n, l }) {
  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.01em' }}>{n}</div>
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85 }}>{l}</div>
    </div>
  );
}

export default LandingScreen;
